import { useEffect, useState, type ReactNode } from "react";
import { holdBoot } from "../../i18n";
import { withChunkTimeout } from "../../../../../shared/lib/moduleLoadFailure";
import { CHUNK_WAIT_MS } from "../../lib/chunkWait";

/*
 * THE BOOKING FLOW'S STEP CHUNKS (rc.12 (a), Web Director). A booking route
 * loads the entry plus, only when reached, these named chunks (vite.config.ts
 * `NAMED_CHUNKS`; scripts/check-book-entry.js holds the list):
 *
 *   guest-code      the guest code step and change email (GuestCodeSteps.tsx)
 *   account-create  account creation (AccountCreate.tsx and what only it uses)
 *
 * Each is PREFETCHED where its step becomes reachable, for the people who can
 * reach it: guest-code when guest Review mounts (GuestReviewRoute);
 * account-create when the sign-in step mounts (BookSignIn), and at entry
 * evaluation for a first URL on /book/create (router/index.tsx).
 *
 * Not React.lazy: lazy suspends on its first render even when the module is
 * already here, so every prefetched step would still commit its fallback
 * first. `WithStepChunk` renders a loaded chunk in the same commit, and the
 * fallback only while a load is really on its way.
 *
 * A failed load is the route's error: thrown in render, RouteError asks
 * build-info, and shows the release notice only if a newer release is live,
 * the honest generic failure otherwise (docs/architecture/stale-build-guard.md).
 * A failed PREFETCH is silent and forgotten; the browser's module map may
 * still remember it, so the step's own load can fail the same way (rc.13 B).
 *
 * Focus is each screen's own, unchanged (c6-3): the fallback is a skeleton
 * whose heading takes no focus, and the step's heading moves focus (or not)
 * as it always did, when it mounts after its chunk.
 */
export interface StepChunk<T> {
  /** The module, once it is here. */
  loaded(): T | null;
  /** Load it (one load for every caller); a failure is forgotten. */
  load(): Promise<T>;
  /** Load it in the background; a failure is silent. */
  prefetch(): void;
}

export function stepChunk<T>(importer: () => Promise<T>): StepChunk<T> {
  let module: T | null = null;
  let pending: Promise<T> | null = null;
  const load = (): Promise<T> => {
    // Bounded (rc.13): a request that never settles becomes a load failure
    // after CHUNK_WAIT_MS, so no skeleton stays up forever.
    pending ??= withChunkTimeout(importer(), CHUNK_WAIT_MS).then(
      (loaded) => {
        module = loaded;
        return loaded;
      },
      (error: unknown) => {
        pending = null;
        throw error;
      }
    );
    return pending;
  };
  return {
    loaded: () => module,
    load,
    prefetch: () => {
      load().catch(() => {});
    },
  };
}

export const guestCodeChunk = stepChunk(() => import("./guest/GuestCodeSteps"));
export const accountCreateChunk = stepChunk(() => import("./AccountCreate"));

/**
 * `children(module)` once the chunk is here, `fallback` until then. Rendering
 * this means the screen NEEDS the chunk, so a Spanish reader's boot waits for
 * it too (holdBoot): one skeleton, then the step.
 */
export function WithStepChunk<T>({ chunk, fallback, children }: { chunk: StepChunk<T>; fallback: ReactNode; children: (module: T) => ReactNode }) {
  const [module, setModule] = useState<T | null>(() => chunk.loaded());
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  useEffect(() => {
    if (module) return;
    let live = true;
    holdBoot(chunk.load()).then(
      (loaded) => {
        if (live) setModule(() => loaded);
      },
      (error: unknown) => {
        if (live) setFailure({ error });
      }
    );
    return () => {
      live = false;
    };
  }, [chunk, module]);
  // To the route's error element, which asks build-info (RouteError).
  if (failure) throw failure.error;
  return <>{module ? children(module) : fallback}</>;
}

/**
 * BEFORE A WRITE THAT LEADS TO A LAZY STEP, ITS CHUNK (rc.12 (a), the Web
 * Director; the same principle as rc.11: nothing is sent that the page cannot
 * follow through on). Asking for a guest code emails a code; if the code
 * step's chunk then failed, the code would be wasted. So the write awaits
 * `ready()` first: true once the chunk is here (at once for a prefetched
 * one), false when it is not, and then nothing is sent, and the failure is
 * thrown in render to the route's error element (which asks build-info).
 */
export function useChunkBeforeWrite<T>(chunk: StepChunk<T>): () => Promise<boolean> {
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  if (failure) throw failure.error;
  return () =>
    chunk.load().then(
      () => true,
      (error: unknown) => {
        setFailure({ error });
        return false;
      }
    );
}
